/**
 * Журнал вхідних ПН (від постачальників).
 *
 * 2026-07-31 (рішення власника — «бухгалтер не має набивати ПН руками»):
 * кнопка «завантажити з ЄРПН» — імпорт ZIP/XML виписки з кабінету ДПС
 * (розділ ЄРПН → Отримані ПН/РК → «Експорт XML в ZIP»): ПН створюються
 * автоматично і АВТО-привʼязуються до приходу/оплати за сумою+датами;
 * непривʼязані — кнопка «привязати» у колонці «Підстава».
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxInvoicesIncoming,
  type TaxInvoiceIncomingListItem,
} from '@/api/taxInvoices';
import { goodsReceipts } from '@/api/goodsReceipts';
import { bankPayments } from '@/api/bankDocs';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


export default function TaxInvoicesIncomingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Стандартні фільтри списків (2026-08-07, як /customer-orders):
  // локальні дати порожні — діє глобальний період шапки.
  const f = useDocListFilters();
  const [kindFilter, setKindFilter] = useState<'ALL' | 'PN' | 'RK'>('ALL');
  // Колонка «Організація» — map id→name (2026-08-22).
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);
  // 2026-08-09 (Норма #29): серверна пагінація + сортування; фільтр
  // ПН/РК — серверний (is_correction).
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<TaxInvoiceIncomingListItem>(
    (limit, offset, sort) => taxInvoicesIncoming.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom?.slice(0, 10),
      date_to: f.dateTo?.slice(0, 10),
      is_correction: kindFilter === 'ALL' ? undefined : kindFilter === 'RK',
    }),
    [kindFilter, ...f.queryKey],
    { attachmentsType: 'tax.tax_invoices_incoming' },
  );

  // UX β: фільтр org dropdown по tax.invoice.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('tax.invoice.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);

  // ── Імпорт виписки ЄРПН ──
  const [erpnOpen, setErpnOpen] = useState(false);
  const [erpnOrg, setErpnOrg] = useState('');
  const [erpnReport, setErpnReport] = useState<Awaited<
    ReturnType<typeof taxInvoicesIncoming.erpnImport>> | null>(null);
  const [erpnError, setErpnError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const importMut = useMutation({
    mutationFn: (file: globalThis.File) => {
      const org = erpnOrg || defaultOrgId || availableOrgs[0]?.id || '';
      return taxInvoicesIncoming.erpnImport(org, file);
    },
    onSuccess: (rep) => {
      setErpnReport(rep); setErpnError(null);
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      refreshRef.current?.();
    },
    onError: (e: any) => setErpnError(e?.response?.data?.detail ?? String(e)),
  });

  // ── Ручна привʼязка підстави ──
  const [linkFor, setLinkFor] = useState<TaxInvoiceIncomingListItem | null>(null);
  const receiptsQ = useQuery({
    queryKey: ['link-receipts', linkFor?.contractor_id],
    queryFn: () => goodsReceipts.list({
      organization_id: linkFor!.organization_id,
      contractor_id: linkFor!.contractor_id, limit: 50,
    }),
    enabled: !!linkFor,
  });
  const paymentsQ = useQuery({
    queryKey: ['link-payments', linkFor?.contractor_id],
    queryFn: () => bankPayments.list({
      organization_id: linkFor!.organization_id,
      contractor_id: linkFor!.contractor_id, limit: 50,
    }),
    enabled: !!linkFor,
  });
  const linkMut = useMutation({
    mutationFn: (p: { type: 'doc.goods_receipts' | 'doc.bank_payments'; id: string }) =>
      taxInvoicesIncoming.update(linkFor!.id, {
        base_document_type: p.type, base_document_id: p.id,
      }),
    onSuccess: () => {
      setLinkFor(null);
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      refreshRef.current?.();
    },
  });

  const cols = useMemo<ColDef<TaxInvoiceIncomingListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: TaxInvoiceIncomingListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    {
      headerName: 'Вид', width: 100, sortable: false,
      cellRenderer: (p: { data?: TaxInvoiceIncomingListItem }) => {
        const r = p.data;
        if (!r) return '';
        return (
          <span title={r.is_correction && r.corrected_number
            ? `Коригує ПН №${r.corrected_number}`
              + (r.corrected_date ? ` від ${new Date(r.corrected_date).toLocaleDateString('uk-UA')}` : '')
            : undefined}>
            {r.is_correction ? 'РК' : 'ПН'}
            {r.sign_status === 'TO_SIGN' && (
              <span style={{ marginLeft: 6, color: 'var(--badge-amber-fg)', background: 'var(--badge-amber-bg)',
                border: '1px solid var(--badge-amber-bd)', borderRadius: 4, padding: '0 4px',
                fontSize: '0.72rem', fontWeight: 700 }}
                title="Отриманий з кабінету ДПС (розділ «Реєстрація ПН-РК») — очікує ВАШОГО підпису і реєстрації">
                на підпис
              </span>
            )}
          </span>
        );
      },
    },
    { field: 'invoice_number', headerName: '№ НН', width: 130, sortable: true },
    {
      field: 'invoice_date', headerName: t('cd.date'), width: 120, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString(i18n.language) : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 200, sortable: true,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    { field: 'contractor_name', headerName: 'Постачальник', flex: 1, minWidth: 200, sortable: false },
    {
      field: 'total_amount', headerName: t('cd.amount'), width: 120, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'base_document_id', headerName: 'Підстава', width: 130, sortable: false,
      cellRenderer: (p: { data?: TaxInvoiceIncomingListItem }) => {
        const r = p.data;
        if (!r) return '';
        return r.is_correction ? (
          <span style={{ color: 'var(--muted)' }}
            title={r.corrected_number
              ? `Коригує ПН №${r.corrected_number}` : 'РК коригує ПН'}>
            {r.corrected_number ? `ПН №${r.corrected_number}` : '—'}
          </span>
        ) : r.base_document_id ? (
          <span style={{ color: 'var(--badge-green-fg)' }}
            title={r.base_document_type === 'doc.bank_payments'
              ? 'Привʼязано до оплати' : 'Привʼязано до приходу'}>
            ✓ {r.base_document_type === 'doc.bank_payments' ? 'оплата' : 'прихід'}
          </span>
        ) : (
          <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
            onClick={(e) => { e.stopPropagation(); setLinkFor(r); }}
            title="Привʼязати ПН до приходу або оплати постачальнику">
            привязати
          </a>
        );
      },
    },
    {
      field: 'is_registered_erpn', headerName: 'ЄРПН', width: 140, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: TaxInvoiceIncomingListItem }) => {
        const r = p.data;
        if (!r) return '';
        const regDate = r.registration_date
          ? ` ${new Date(r.registration_date).toLocaleDateString('uk-UA')}` : '';
        if (!r.is_registered_erpn) {
          return (
            <span style={{ fontWeight: 600, color: 'var(--badge-red-fg)', cursor: 'help' }}
              title="ПН не зареєстрована постачальником в ЄРПН — права на податковий кредит немає">
              ✗ не зареєстр.
            </span>
          );
        }
        if (r.external_source === 'erpn') {
          return (
            <span style={{ fontWeight: 600, color: 'var(--badge-green-fg)', cursor: 'help' }}
              title={`Підтверджено випискою з ЄРПН — реєстрація${regDate}`}>
              ✓ ЄРПН{regDate}
            </span>
          );
        }
        return (
          <span style={{ color: 'var(--badge-green-fg)', cursor: 'help' }}
            title={`Зазначено вручну (зі слів постачальника)${regDate ? `, дата реєстрації${regDate}` : ''} — звірте з ЄРПН (вкладка «Отримати ПН» у Кабінеті ДПС)`}>
            ✓ вручну{regDate}
          </span>
        );
      },
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 110, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [t, i18n.language, orgName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>ПН/РК (вхідні)<TitleTotal total={total} /></h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => { setErpnReport(null); setErpnError(null); setErpnOpen(true); }}
            disabled={!hasPermissionInAnyOrg}
            title="Імпорт ZIP/XML виписки з кабінету ДПС (ЄРПН → Отримані ПН/РК → Експорт XML в ZIP)">
            завантажити з ЄРПН
          </button>
          <button
            className="primary"
            onClick={() => navigate('/tax-invoices-incoming/new')}
            disabled={!hasPermissionInAnyOrg}
            title={!hasPermissionInAnyOrg
              ? `У вас немає прав створювати податкові накладні у жодній з ${totalAccessibleOrgs} ваших організацій`
              : undefined
            }
          >
            + Нова вхідна ПН
          </button>
        </div>
      </div>
      <DocListFiltersRow>
        <select value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as 'ALL' | 'PN' | 'RK')}
          style={{ width: 110, flex: '0 0 auto' }}>
          <option value="ALL">Вид: усі</option>
          <option value="PN">ПН</option>
          <option value="RK">РК</option>
        </select>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<TaxInvoiceIncomingListItem>
        deleteRow={{ remove: (id) => taxInvoicesIncoming.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="tax-invoices-incoming"
        onRowDoubleClicked={(r) => navigate(`/tax-invoices-incoming/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        refreshRef={refreshRef}
      />

      {erpnOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setErpnOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '80vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Завантаження вхідних ПН з ЄРПН</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setErpnOpen(false)}>закрити</button>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>
              У кабінеті ДПС: розділ <b>ЄРПН → Отримані ПН/РК</b> → оберіть
              період → <b>«Експорт XML в ZIP»</b>. Отриманий архів завантажте
              сюди — ПН створяться автоматично і привʼяжуться до приходів/
              оплат за сумою і датами.
            </p>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Організація</label>
                <select value={erpnOrg || defaultOrgId || availableOrgs[0]?.id || ''}
                  onChange={(e) => setErpnOrg(e.target.value)}>
                  {availableOrgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Файл виписки (ZIP або XML)</label>
                <input type="file" ref={fileRef} accept=".zip,.xml" />
              </div>
            </div>
            <button className="primary" disabled={importMut.isPending}
              onClick={() => {
                const file = fileRef.current?.files?.[0];
                if (file) importMut.mutate(file);
              }}>
              {importMut.isPending ? 'імпортується…' : 'імпортувати'}
            </button>
            {erpnError && (
              <div style={{ color: '#dc2626', marginTop: 8, whiteSpace: 'pre-wrap' }}>{erpnError}</div>
            )}
            {erpnReport && (
              <div style={{ marginTop: 12, fontSize: '0.9rem' }}>
                <p style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>
                  ✓ Створено: {erpnReport.created} (привʼязано авто:
                  {' '}{erpnReport.matched}); вже були: {erpnReport.skipped_existing}
                </p>
                {erpnReport.unmatched.length > 0 && (
                  <details open>
                    <summary style={{ color: 'var(--badge-amber-fg)' }}>
                      Без підстави ({erpnReport.unmatched.length}) — привʼяжіть
                      кнопкою «привязати» у списку
                    </summary>
                    <ul>{erpnReport.unmatched.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </details>
                )}
                {erpnReport.no_contractor.length > 0 && (
                  <details open>
                    <summary style={{ color: '#dc2626' }}>
                      Постачальника не знайдено ({erpnReport.no_contractor.length})
                      — створіть контрагента з цим ІПН і повторіть імпорт
                    </summary>
                    <ul>{erpnReport.no_contractor.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </details>
                )}
                {erpnReport.not_ours.length > 0 && (
                  <details>
                    <summary style={{ color: 'var(--muted)' }}>
                      Пропущено видані нами ({erpnReport.not_ours.length})
                    </summary>
                    <ul>{erpnReport.not_ours.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </details>
                )}
                {erpnReport.errors.length > 0 && (
                  <details open>
                    <summary style={{ color: '#dc2626' }}>Помилки ({erpnReport.errors.length})</summary>
                    <ul>{erpnReport.errors.map((s, i) => <li key={i}>{s}</li>)}</ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {linkFor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setLinkFor(null)}>
          <div className="card" style={{ width: 680, maxHeight: '75vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>
                Привʼязати ПН №{linkFor.invoice_number} ({fmt(linkFor.total_amount)} грн)
              </h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setLinkFor(null)}>закрити</button>
            </div>
            <h4 style={{ margin: '0.5rem 0 0.25rem' }}>Приходи постачальника</h4>
            {(receiptsQ.data?.items ?? []).length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Немає приходів.</p>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <tbody>
                {(receiptsQ.data?.items ?? []).map((rc) => (
                  <tr key={rc.id} style={{ borderBottom: '1px solid var(--border-soft)', cursor: 'pointer' }}
                      onClick={() => linkMut.mutate({ type: 'doc.goods_receipts', id: rc.id })}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '0.35rem' }}>№{rc.document_number}</td>
                    <td style={{ padding: '0.35rem' }}>
                      {new Date(rc.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ padding: '0.35rem', textAlign: 'right',
                      fontWeight: Math.abs(Number(rc.total_amount) - Number(linkFor.total_amount)) < 0.02 ? 700 : 400 }}>
                      {fmt(rc.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Оплати постачальнику</h4>
            {(paymentsQ.data?.items ?? []).length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Немає оплат.</p>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <tbody>
                {(paymentsQ.data?.items ?? []).map((bp) => (
                  <tr key={bp.id} style={{ borderBottom: '1px solid var(--border-soft)', cursor: 'pointer' }}
                      onClick={() => linkMut.mutate({ type: 'doc.bank_payments', id: bp.id })}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '0.35rem' }}>№{bp.document_number}</td>
                    <td style={{ padding: '0.35rem' }}>
                      {new Date(bp.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ padding: '0.35rem', textAlign: 'right',
                      fontWeight: Math.abs(Number(bp.amount) - Number(linkFor.total_amount)) < 0.02 ? 700 : 400 }}>
                      {fmt(bp.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 8 }}>
              Жирним — сума збігається з ПН. Клік по рядку привʼязує.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
